<template lang="pug">
.relative.redesign.ui-design(
  :class="{'pt-6': isRouteEditor}"
)
  .section-container.relative(v-if="eventData")
    v-overlay.filter-blur-1.pointer-events-none(
      v-if="isSave"
      absolute
      color="redesignBackground"
      opacity="0.1"
      :z-index="999"
    )

    event-sections(
      v-if="filteredSections.length"
      ref="eventSections"
      :sectionsData="filteredSections"
      :event="eventData"
      :isEditable="isEditable"
      :event-manager-access="eventManagerAccess"
      @saveSections="saveSections"
      @restoreSections="setDefaultSections"
      @changeCanBeEditable="changeCanBeEditable"
    )

    section-footer(v-if="!isRouteEditor" :event="eventData")
  .section-container__preload(v-if="isLoadingPage")
    text-loader(
      text="..."
      :is-init="false"
    )
</template>

<script>
import SectionMain from '@/modules/UserEventLanding/components/event-landing/SectionMain.vue';
import SectionFooter from '@/modules/UserEventLanding/components/event-landing/SectionFooter.vue';
import EventSections from './EventSections.vue';
import TextLoader from '@/components/user-redesign/layout/TextLoader.vue';
import EventPagesService from '@/services/API/event-pages';
import { SECTION_TYPES } from '@/modules/UserEventLanding/constants';
// import { PERMISSIONS } from '@/constants/permissions';

// CARD_TYPES
const DEFAULT_PAGE_SECTIONS = [
  {
    active: true,
    data: { enabled: true },
    type: SECTION_TYPES.MAIN,
  },
  {
    active: true,
    data: { enabled: true },
    type: SECTION_TYPES.ABOUT,
  },
  {
    active: true,
    cards: [],
    data: { enabled: false },
    type: SECTION_TYPES.GENERIC,
  },
  {
    active: true,
    data: { enabled: true },
    type: SECTION_TYPES.UNITS,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.EXPERTS,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.RESULTS,
  },
  {
    active: true,
    data: { enabled: true },
    type: SECTION_TYPES.MEMBERS_INFO,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.VIDEO_PRESENTATION,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.GALLERY,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.EXHIBITION,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.NEWS,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.ASSOCIATIONS,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.PARTNERS,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.AWARDS,
  },
  {
    active: true,
    data: { enabled: false },
    type: SECTION_TYPES.VOTE,
  },
];

export default {
  name: 'EventSectionContainer',

  components: {
    SectionMain,
    SectionFooter,
    EventSections,
    TextLoader,
  },

  props: {
    availableSectionsTypes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      eventData: null,
      sections: [],
      isSave: false,
      isLoadingPage: true,
      canBeEditable: true,
    };
  },

  computed: {
    defaultPageSections() {
      return DEFAULT_PAGE_SECTIONS.filter((section) => section.active);
    },
    isEditable() {
      const { canBeEditable, eventData, $user } = this;
      const { isOrganization, isAdmin } = $user;
      return canBeEditable &&
        ((isOrganization || isAdmin) && eventData.isOwner);
    },
    eventManagerAccess() {
      const { canBeEditable, eventData, $user } = this;
      const { isEventManager, eventManagerOrganizer } = $user;
      return canBeEditable &&
        (isEventManager && eventData?.owner_id === eventManagerOrganizer?.user_id);
    },
    isRouteEditor() {
      return !!this.$route.meta.editor;
    },
    filteredSections() {
      return this.sections
        .filter((section) => this.availableSectionsTypes.length
          ? this.availableSectionsTypes.includes(section.type)
          : true)
        .filter((section) => !(this.isEditable || this.eventManagerAccess) ? (section.data?.enabled || false) : true)
        .filter((section) => !(this.isEditable || this.eventManagerAccess) && section?.type === SECTION_TYPES.GENERIC
          ? section?.cards?.length
          : true);
    },
  },

  created() {
    this.getEvent().then(() => {
      if (!this.eventData) {
        return;
      }

      this.getCustomSections().then(() => {
        if (!this.sections.length) {
          this.setDefaultSections();
        }
      });
    });
  },

  mounted() {
    window.addEventListener('beforeunload', this.preventNav);
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('beforeunload', this.preventNav);
    });
    setTimeout(() => {
      this.isLoadingPage = false;
    }, 1000);
  },

  beforeRouteLeave(_, __, next) {
    if (this.hasUnsavedSectionsChanges()) {
      this.confirmSavingChanges(next);
      return;
    }
    next();
  },

  beforeDestroy() {
    this.$store.dispatch('eventConstructor/setEvent', null);
  },

  methods: {
    hasUnsavedSectionsChanges() {
      const { eventSections } = this.$refs;
      return !!(eventSections && eventSections.hasUnsavedChanges());
    },
    saveChangesInSections() {
      const { eventSections } = this.$refs;
      if (eventSections) eventSections.saveSections();
    },
    confirmSavingChanges(next = () => ({})) {
      this.$dialogs.confirm({
        attrs: {
          method: () => this.saveChangesInSections(),
          isRequest: false,
          message: 'У вас есть несохраненные изменения.<br/>Вы действительно хотите закрыть редактирование мероприятия без сохранения?',
          closeBtnAttrs: {
            'btn-text': 'Выйти без сохранения',
          },
          confirmBtnAttrs: {
            'btn-text': 'Сохранить изменения',
          },
        },
        listeners: {
          'before:close': next,
        },
      });
    },
    preventNav(e) {
      if (this.hasUnsavedSectionsChanges()) {
        this.confirmSavingChanges();
        e.preventDefault();
        e.returnValue = true;
      }
    },
    async getEvent() {
      const { id } = this.$route.params;
      const params = {
        expand: 'options,organization_name,active_members_count,nominations,main_image,images,'
          + 'request_status,assigned_regions_count,assigned_experts_count,all_event_members_count,'
          + 'assigned_organizers_count,templates,highest_event,experts,telegram,stages_count,'
          + 'event_user_requests,qr_link,organization',
      };
      const args = [id];

      const { item } = await this.$models.event.objects.show({
        args,
        params,
      });

      const promises = [
        item.fetchImages(),
      ];
      if (item.isTeamEvent && item.isActiveEventMember) {
        const teamSearchParams = {
          'search[assign_user_id]': this.$user?.data?.id,
        };
        promises.push(item.getTeams(teamSearchParams));
      }
      await Promise.all(promises);
      this.eventData = item;
      this.checkRedirectToLanding();
      this.$store.dispatch('eventConstructor/setEvent', this.eventData);
    },

    checkRedirectToLanding() {
      if (
        this.$route.name === 'event-landing-redesign'
        || this.isEditable
        || this.eventManagerAccess
        || this.$user.isAdmin
      ) return;
      this.$router.push({
        name: 'event-landing-redesign',
        params: {
          id: this.eventData.id,
        },
      });
    },

    async getCustomSections() {
      const { data } = await EventPagesService.getPages(this.eventData.id, { expand: 'cards' });
      this.sections = data.data;
    },

    async saveSections(savedSections) {
      this.isSave = true;
      const params = {
        sections: savedSections.length ? savedSections : null,
        expand: 'cards',
      };
      try {
        const { data } = await EventPagesService.savePages(this.eventData.id, params);
        this.sections = JSON.parse(JSON.stringify(data.data));
        this.$toast.success('Изменения конструктора сохранены', { timeout: 3000 });
      } catch (error) {
        this.$toast.error('Не удалось сохранить', { timeout: 3000 });
      } finally {
        this.isSave = false;
      }
    },

    setDefaultSections() {
      this.sections = this.defaultPageSections;
    },

    changeCanBeEditable() {
      this.canBeEditable = !this.canBeEditable;
    },
  },
};
</script>

<style lang="scss" scoped>
.section-container {
  &__preload {
    position: absolute;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #{$redesignControlsBackground};
  }
}
</style>
